import type { JobMetadata, PositionView, ResetOutcome } from "@/types/api";

export interface ResetAvailability {
  allowed: boolean;
  reason?: string;
}

/**
 * Why a source's position can't be reset, or `undefined` when it can. The
 * engine's own rule: only a scanner's and a change stream's stored position
 * is theirs to reset.
 */
function refusal(provider: string): string | undefined {
  switch (provider) {
    case "scanner":
    case "mongodb":
      return undefined;
    case "kafka":
      return "A Kafka source resumes from its consumer group's committed offsets, which a reset doesn't change. Use a new consumer group to start elsewhere.";
    case "postgres_cdc":
      return "Change capture resumes from its replication slot, which a reset doesn't move. To start over, capture through a new slot and backfill with a scanner.";
    default:
      return "This source stores no position, so there is nothing to reset.";
  }
}

/**
 * Whether the pipeline's position can be reset now: a source that keeps a
 * resettable position, on a pipeline that has stopped. Starting and stopping
 * are not stopped: a worker may still be reading.
 */
export function resetAvailability(
  job: JobMetadata,
  provider: string,
): ResetAvailability {
  const reason = refusal(provider);
  if (reason) return { allowed: false, reason };
  const state = String(job.state).toLowerCase();
  if (state !== "stopped" && state !== "failed") {
    return {
      allowed: false,
      reason:
        "Stop the pipeline first: a position is reset only once nothing reads it.",
    };
  }
  return { allowed: true };
}

/**
 * A new operation id for one confirmed reset. Kept for that confirmation, so
 * a retry after a lost reply continues the same reset instead of starting
 * another.
 */
export function newResetOpId(): string {
  return crypto.randomUUID();
}

/** What confirming a reset does, said before it is confirmed. */
export interface ResetSummary {
  /** What is deleted, or that nothing is stored. */
  removes: string;
  /** Where the next start reads from once the position is gone. */
  nextStart?: string;
  /** What that start means for records, for the source kind. */
  consequence?: string;
  /** Why the engine refuses to reset this pipeline, in its words. */
  refused?: string;
}

const ENGINE_RESET_PREFIX = "forgets the stored position; the next start then ";

const CONSEQUENCES: Record<string, string> = {
  scanner: "Records already delivered are delivered again.",
  mongodb: "Changes made while the pipeline was stopped are not read.",
};

function plural(count: number, one: string): string {
  return `${count.toLocaleString("en-US")} ${one}${count === 1 ? "" : "s"}`;
}

// A scanner keeps its progress apart from checkpoints, so a scanner
// pipeline can have a position to reset and no checkpoints at all.
function storedParts(view: PositionView): string[] {
  const parts: string[] = [];
  if (view.scan) {
    const read = plural(view.scan.documents_emitted, "record");
    parts.push(`the scanner's saved progress (${read} read so far)`);
  }
  if (view.checkpoints > 0) {
    parts.push(plural(view.checkpoints, "saved checkpoint"));
  }
  return parts;
}

function checkpointNote(view: PositionView): string {
  return view.scan && view.checkpoints === 0
    ? " The pipeline has no checkpoints; the scanner's progress is stored apart from them."
    : "";
}

/**
 * The reset dialog's account of a pipeline's position: what the reset
 * deletes and where the next start goes. The engine describes the start in
 * words; its sentence is reused, and shown whole when it is a refusal.
 */
export function resetSummary(view: PositionView): ResetSummary {
  const parts = storedParts(view);
  const removes = parts.length
    ? `This deletes ${parts.join(" and ")}.${checkpointNote(view)}`
    : "Nothing is stored yet, so the reset changes nothing.";
  if (!view.reset.startsWith(ENGINE_RESET_PREFIX)) {
    return { removes, refused: view.reset };
  }
  return {
    removes,
    nextStart: view.reset.slice(ENGINE_RESET_PREFIX.length),
    consequence: parts.length ? CONSEQUENCES[view.provider] : undefined,
  };
}

/** What a finished reset removed, for the confirmation toast. */
export function resetOutcomeMessage(outcome: ResetOutcome): string {
  if (outcome.already_done) return "This reset had already finished.";
  const removed: string[] = [];
  if (outcome.checkpoints_removed > 0) {
    removed.push(plural(outcome.checkpoints_removed, "checkpoint"));
  }
  if (outcome.scan_position_removed) removed.push("the scan position");
  const what = removed.length
    ? `Removed ${removed.join(" and ")}.`
    : "Nothing was stored to remove.";
  return `${what} The next start ${outcome.next_start}.`;
}
